import { FC } from "react";
import {
  currencify,
  formatMonthYearShort,
  formatNumber,
} from "@repo/shared-utils/num-utils";
import {
  IBalanceSheet,
  ICashFlow,
  IKeyRatio,
  IRevenue,
} from "../../interfaces/StockFinancials.interface";
import { TRADING_MALL_TABLE_VALUE_MAP } from "./StockFinancials.constants";
import { sortObjectByKeySuffix } from "@repo/shared-utils/common-utils";

interface HeaderAndColumnsItem {
  [key: string]: string;
}

interface StockFinancialsTableProps {
  header: HeaderAndColumnsItem[];
  columns: HeaderAndColumnsItem[];
  stockFinancials: IKeyRatio[] | IBalanceSheet[] | ICashFlow[] | IRevenue[];
  selectedPeriod: number | string | null;
}

const StockFinancialsTable: FC<StockFinancialsTableProps> = ({
  header,
  columns,
  stockFinancials: allStockFinancials,
  selectedPeriod,
}) => {
  const stockFinancials = allStockFinancials.find(
    (item) => item?.date == selectedPeriod,
  );

  const orderedArray = sortObjectByKeySuffix(columns[0] || {}, "item") || [];

  if (!stockFinancials) {
    return <p>No data available for the selected period.</p>;
  }

  return (
    <table className="w-full border-separate border-spacing-0 overflow-hidden rounded-[10px] border border-gray-200">
      <thead className="bg-lgray-200">
        <tr>
          {Object.values(header?.[0] || {})?.map((item: any, index: number) => {
            return (
              <th
                key={index}
                className={`w-[50%] border border-gray-200 py-2 text-left font-medium ${
                  index === 0 ? "md:w-[28%]" : "md:w-[72%]"
                }`}
              >
                <h4 className="ml-2 text-xs md:text-base">
                  {(() => {
                    const monthYear = selectedPeriod
                      ? formatMonthYearShort(Number(selectedPeriod))
                      : "Mar 24";
                    const keys = [
                      "tableColumnTitleText",
                      "tableColumnTitleTextKR",
                      "tableColumnTitleTextBS",
                      "tableColumnTitleTextCF",
                      "tableColumnTitleTextCT",
                    ];
                    const source = Array.isArray(item) ? item[0] : item;

                    for (const key of keys) {
                      const value = source?.[key];
                      if (value) return value.replace("March 24", monthYear);
                    }

                    return null;
                  })()}
                </h4>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {(orderedArray || []).map((itemValueRaw) => {
          const itemValue = itemValueRaw as Record<string, any>;
          const itemKey =
            itemValue?.tableRowTitleText ||
            itemValue?.tableRowTitleTextKR ||
            itemValue?.tableRowTitleTextBS ||
            itemValue?.tableRowTitleTextCF ||
            itemValue?.tableRowTitleTextCT ||
            "";

          const label: string =
            (itemValue.tableRowTitleText as string) ||
            (itemValue.tableRowTitleTextKR as string) ||
            (itemValue.tableRowTitleTextBS as string) ||
            (itemValue.tableRowTitleTextCF as string) ||
            (itemValue.tableRowTitleTextCT as string) ||
            itemKey;

          const dataKey =
            TRADING_MALL_TABLE_VALUE_MAP[
              label as keyof typeof TRADING_MALL_TABLE_VALUE_MAP
            ] || itemKey;
          const value = (
            stockFinancials as unknown as Record<string, unknown>
          )?.[dataKey];
          const percentageAdd = [
            "npm",
            "etr",
            "roeAnnual",
            "roceAnnual",
            "roa",
            "roc",
            "eam",
          ];
          const nothingToAdd = ["tdtea"];
          return (
            <tr key={itemKey}>
              <td className="border border-gray-200 p-3">
                <h4 className="text-xs font-medium md:text-base">{label}</h4>
              </td>
              <td className="border border-gray-200 p-3">
                <h4 className="text-xs md:text-base">
                  {(() => {
                    if (!value) return "-";

                    if (nothingToAdd.includes(dataKey)) {
                      return formatNumber(value as number);
                    }

                    if (percentageAdd.includes(dataKey)) {
                      return `${formatNumber(value as number)}%`;
                    }

                    return value.toString().includes("₹")
                      ? `${formatNumber(value as number)}`
                      : `${currencify(value as number, {
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 2,
                        })}`;
                  })()}
                </h4>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
export default StockFinancialsTable;
